import { useMemo } from 'react'
import { ChartWidget } from 'common/components/DataViz/components/ChartWidget/ChartWidget'
import type { ChartWidgetProps } from 'common/components/DataViz/components/ChartWidget/ChartWidget.types'
import type { GeneratedAxisChartProps } from 'common/components/DataViz/components/GeneratedAxisChart/GeneratedAxisChart.types'
import type { DataVizBaseProps } from 'common/components/DataViz/DataViz.types'
import type { UseChartSettingsProps, UseChartSettingsClickHandler } from 'common/components/DataViz/hooks/useChartSettings/useChartSettings.types'
import type { UseParallelApiCallsChunkStats, UseParallelApiCallsResult } from '../useParallelApiCalls'
import { ChunkLines, ChunkLabels, ChunkSetItem } from './ChunkLines'
import { responseRows } from './js/chunkRows'
import './css/Demo.css'

export interface LoadChartSettings extends Pick<ChartWidgetProps, 'axes' | 'legend_items' | 'grouping_key' | 'colors' | 'time_zone'>,
  Pick<DataVizBaseProps, 'handleZoom' | 'selected'> {
  handleClick?: UseChartSettingsClickHandler;
  toRows?: (response?: Record<string, unknown>) => Record<string, unknown>[];
}

export interface LoadChartProps extends LoadChartSettings {
  data: UseParallelApiCallsResult[];
  chunks: UseParallelApiCallsChunkStats[];
  start_time: number;
  end_time: number;
}

const SVG_RENDERERS = [ChunkLines]
const HTML_RENDERERS = [ChunkLabels]

const keepAllRows = (): undefined => undefined

export const LoadChart = ({
  data,
  chunks,
  start_time,
  end_time,
  axes,
  colors,
  toRows = responseRows,
  handleZoom,
  grouping_key,
  legend_items,
  time_zone,
  selected,
  handleClick
}: LoadChartProps): JSX.Element => {
  const time_key = (axes?.bottom ?? axes?.top)?.value_key

  const selection: UseChartSettingsProps = handleClick ? { selected, handleClick } : {}

  const visible_chunks = useMemo((): ChunkSetItem[] => chunks.flatMap((chunk, index) => {
    if (chunk.end_time <= start_time || chunk.start_time >= end_time) return []
    const rows = toRows(data[index]?.data).filter(row => {
      const time = time_key ? new Date(String(row[time_key])).getTime() : NaN
      return time >= start_time && time <= end_time
    })
    return [{
      start_time: Math.max(chunk.start_time, start_time),
      end_time: Math.min(chunk.end_time, end_time),
      status: chunk.status,
      data: rows
    }]
  }), [chunks, data, toRows, time_key, start_time, end_time])

  const handleAxisZoom: GeneratedAxisChartProps['handleZoom'] = (zoom_data, type) => {
    const domain = zoom_data?.bottom?.domain
    if (type === 'zoom' && domain) handleZoom?.([Number(domain[0]), Number(domain[1])])
  }

  const flat_data = useMemo(() => visible_chunks.flatMap(chunk => chunk.data), [visible_chunks])

  const chunk_lines = useMemo((): ChunkSetItem[] => visible_chunks.map((chunk, index) => {
    const next_chunk = visible_chunks[index + 1]
    const next_row = next_chunk?.status === 'done' ? next_chunk.data[0] : undefined
    return { ...chunk, data: next_row && chunk.data.length ? [...chunk.data, next_row] : chunk.data }
  }), [visible_chunks])

  const chunk_segments = useMemo(() => visible_chunks.reduce<ChunkSetItem[]>((segment_list, chunk) => {
    const last_segment = segment_list[segment_list.length - 1]
    if (chunk.status === 'done' && last_segment?.status === 'done' && last_segment.end_time === chunk.start_time) {
      last_segment.end_time = chunk.end_time
      last_segment.data = [...last_segment.data, ...chunk.data]
      return segment_list
    }
    segment_list.push({ ...chunk })
    return segment_list
  }, []), [visible_chunks])

  return (
    <div className='demo-load-chart'>
      <ChartWidget
        data={flat_data}
        start_time={start_time}
        end_time={end_time}
        axes={axes?.left && !flat_data.length ? { ...axes, left: { ...axes.left, domain: [0, 100] } } : axes}
        SVG={SVG_RENDERERS}
        HTML={HTML_RENDERERS}
        chunk_lines={chunk_lines}
        chunk_segments={chunk_segments}
        colors={colors}
        time_zone={time_zone}
        grouping_key={grouping_key}
        legend={legend_items?.length ? undefined : false}
        legend_items={legend_items}
        {...selection}
        checkIfFiltered={keepAllRows}
        filter_y
        tooltip={{ line_x: true, line_y: true }}
        {...(handleZoom ? { zoom_x: true, zoom: false, handleZoom: handleAxisZoom } : {})}
      />
    </div>
  )
}
